/*
 * Copyright (c) 2019. Legalbird eine Marke der R&S Internet Jewels GmbH
 */
import _has from "lodash/has";
import _pick from "lodash/pick";
import _merge from "lodash/merge";
import { applicationStatuses } from "./applicationStatus";
import formSteps from "./formSteps";
import formStepsFlexService from "./flexService/formSteps";
import {
  fromBoolean,
  fromDate,
  fromFloat,
  fromInsurer,
  sanitizeAddressData,
  sanitizePersonData,
  toBoolean,
  toDate,
  toFloat,
  toInsurer,
} from "@/src/services/formServiceFunctions";
import _forEach from "lodash/forEach";
import _includes from "lodash/includes";
import _set from "lodash/set";
import _get from "lodash/get";
import ApiClient from "@/src/services/apiClient";
import _cloneDeep from "lodash/cloneDeep";
import { getProductData } from "@/src/services/productService";
import { FormServiceBase, FormServiceSaveProps } from "@/src/types/FormServiceBase";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import { Customer } from "@/src/types/Entities/Customer";
import { isFlexServiceProduct } from "@/src/services/advocardService";

type ApplicationStatus = typeof applicationStatuses[keyof typeof applicationStatuses];

class FormService extends FormServiceBase<ApplicationStatus> {
  booleanFields = [
    "areSeparated",
    "areDivorced",
    "legalExpenseInsurance",
    "hasMarriageRelatedDisadvantages",
    "alimonyPetitioner.hasMarriageRelatedDisadvantages",
    "alimonyPartner.hasMarriageRelatedDisadvantages",
  ];

  numberFields = ["numberOfChildren"];

  dataFields = (): { [k in ApplicationStatus]?: { [key: string]: string[] } } => ({
    [applicationStatuses.APPLICATION_STATUS_BASIC]: {
      alimony: ["basicChoice"],
    },
    [applicationStatuses.APPLICATION_STATUS_ALIMONY]: {
      alimony: ["dateMarriage", "areSeparated", "dateSeparation", "areDivorced", "dateDivorce", "numberOfChildren"],
    },
    [applicationStatuses.APPLICATION_STATUS_PARTNER]: {
      alimonyPartner: [
        "employmentStatus",
        "netIncome",
        "otherIncome",
        "livingExpenses",
        "monthlyMortgagePayment",
        "monthlyImputedRent",
        "livingSituation",
        "expensesList",
        "hasMarriageRelatedDisadvantages",
      ],
      "alimonyPartner.person": ["gender", "givenName", "familyName"],
    },
    [applicationStatuses.APPLICATION_STATUS_PETITIONER]: {
      alimonyPetitioner: [
        "employmentStatus",
        "netIncome",
        "otherIncome",
        "livingExpenses",
        "monthlyMortgagePayment",
        "monthlyImputedRent",
        "livingSituation",
        "expensesList",
        "hasMarriageRelatedDisadvantages",
      ],
      "alimonyPetitioner.person": ["gender", "givenName", "familyName"],
    },
    [applicationStatuses.APPLICATION_STATUS_CHILDREN]: {
      children: ["name", "birthDate", "childOf", "livesWith", "childAllowanceReceiver"],
    },
    [applicationStatuses.APPLICATION_STATUS_MESSAGE]: {
      alimony: ["personalTextMessage"],
    },
    [applicationStatuses.APPLICATION_STATUS_DOCUMENTS]: {
      alimony: [],
    },
    [applicationStatuses.APPLICATION_STATUS_CONTACT]: {
      "alimonyPetitioner.person": ["gender", "givenName", "familyName"],
      "alimonyPetitioner.person.addresses[0]": ["streetAddress", "postalCode", "addressLocality"],
      customer: ["telephone"],
    },
    [applicationStatuses.APPLICATION_STATUS_INSURANCE]: {
      caseInsurance: ["insurer", "caseReferenceNumber", "insurancePolicyNumber", "policyHolderType"],
      "caseInsurance.policyHolder": ["gender", "givenName", "familyName"],
    },
    [applicationStatuses.APPLICATION_STATUS_SUMMARY]: {
      alimony: ["personalTextMessage"],
    },
  });

  conversionFields = [
    {
      path: "caseInsurance.insurer",
      convertFrom: fromInsurer,
      convertTo: toInsurer,
    },
    {
      path: "dateMarriage",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "dateSeparation",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "dateDivorce",
      convertFrom: fromDate,
      convertTo: toDate,
    },
    {
      path: "netIncome",
      convertFrom: fromFloat,
      convertTo: toFloat,
    },
    {
      path: "otherIncome",
      convertFrom: fromFloat,
      convertTo: toFloat,
    },
    {
      path: "livingExpenses",
      convertFrom: fromFloat,
      convertTo: toFloat,
    },
    {
      path: "otherExpenses",
      convertFrom: fromFloat,
      convertTo: toFloat,
    },
    {
      path: "monthlyMortgagePayment",
      convertFrom: fromFloat,
      convertTo: toFloat,
    },
    {
      path: "monthlyImputedRent",
      convertFrom: fromFloat,
      convertTo: toFloat,
    },
    {
      path: "alimonyPetitioner.netIncome",
      convertFrom: fromFloat,
      convertTo: toFloat,
    },
    {
      path: "alimonyPartner.netIncome",
      convertFrom: fromFloat,
      convertTo: toFloat,
    },
    {
      path: "alimonyPetitioner.otherIncome",
      convertFrom: fromFloat,
      convertTo: toFloat,
    },
    {
      path: "alimonyPartner.otherIncome",
      convertFrom: fromFloat,
      convertTo: toFloat,
    },
    {
      path: "alimonyPetitioner.livingExpenses",
      convertFrom: fromFloat,
      convertTo: toFloat,
    },
    {
      path: "alimonyPartner.livingExpenses",
      convertFrom: fromFloat,
      convertTo: toFloat,
    },
    {
      path: "alimonyPetitioner.otherExpenses",
      convertFrom: fromFloat,
      convertTo: toFloat,
    },
    {
      path: "alimonyPartner.otherExpenses",
      convertFrom: fromFloat,
      convertTo: toFloat,
    },
    {
      path: "alimonyPetitioner.monthlyMortgagePayment",
      convertFrom: fromFloat,
      convertTo: toFloat,
    },
    {
      path: "alimonyPartner.monthlyMortgagePayment",
      convertFrom: fromFloat,
      convertTo: toFloat,
    },
    {
      path: "alimonyPetitioner.monthlyImputedRent",
      convertFrom: fromFloat,
      convertTo: toFloat,
    },
    {
      path: "alimonyPartner.monthlyImputedRent",
      convertFrom: fromFloat,
      convertTo: toFloat,
    },
  ];

  save = async ({ formData, applicationStatus, nextApplicationStatus, product, customer, updateMutation }: FormServiceSaveProps<ApplicationStatus>) => {
    const fields = this.dataFields()[applicationStatus] || {};

    if (_has(fields, "customer")) {
      const customerData = _pick(formData.customer, fields.customer);
      await updateMutation.mutateAsync({
        uri: "/customers",
        id: customer.id,
        data: customerData,
      });
    }

    if (_has(fields, "alimonyPetitioner.person")) {
      let petitionerData = _pick(formData.alimonyPetitioner.person, fields["alimonyPetitioner.person"]);
      petitionerData = sanitizePersonData(petitionerData);
      await updateMutation.mutateAsync({
        uri: "/people",
        id: product.alimonyPetitioner.person.id,
        data: petitionerData,
      });
      await updateMutation.mutateAsync({
        uri: "/people",
        id: product.petitioner.id,
        data: petitionerData,
      });
    }

    if (_has(fields, "alimonyPetitioner.person.addresses[0]")) {
      let addressData = _pick(formData.alimonyPetitioner.person.addresses[0], fields["alimonyPetitioner.person.addresses[0]"]);
      addressData = sanitizeAddressData(addressData);
      await updateMutation.mutateAsync({
        uri: "/addresses",
        id: product.alimonyPetitioner.person.addresses[0].id,
        data: addressData,
      });
      await updateMutation.mutateAsync({
        uri: "/addresses",
        id: product.petitioner.addresses[0].id,
        data: addressData,
      });
    }

    if (_has(fields, "caseInsurance")) {
      const caseInsuranceData = _pick(formData.caseInsurance, fields.caseInsurance);
      await ApiClient.patch("/case_insurances/" + product.caseInsurance.id, { body: JSON.stringify(caseInsuranceData) });
    }

    if (_has(fields, "alimonyPartner.person")) {
      let partnerData = _pick(formData.alimonyPartner.person, fields["alimonyPartner.person"]);
      partnerData = sanitizePersonData(partnerData);
      await updateMutation.mutateAsync({
        uri: "/people",
        id: product.alimonyPartner.person.id,
        data: partnerData,
      });
    }

    if (_has(fields, "alimonyPetitioner")) {
      const alimonyPetitionerData = this.handleAlimonyFields(_pick(formData.alimonyPetitioner, fields.alimonyPetitioner));

      await updateMutation.mutateAsync({
        uri: "/alimony_people",
        id: product.alimonyPetitioner.id,
        data: alimonyPetitionerData,
      });
    }

    if (_has(fields, "alimonyPartner")) {
      const alimonyPartnerData = this.handleAlimonyFields(_pick(formData.alimonyPartner, fields.alimonyPartner));
      await updateMutation.mutateAsync({
        uri: "/alimony_people",
        id: product.alimonyPartner.id,
        data: alimonyPartnerData,
      });
    }

    if (_has(fields, "children")) {
      _forEach(formData.children, async (alimonyChild) => {
        const alimonyChildData = _pick(alimonyChild, fields.children);

        alimonyChildData.birthDate = toDate(alimonyChildData.birthDate);

        await updateMutation.mutateAsync({
          uri: "/alimony_children",
          id: alimonyChild.id,
          data: alimonyChildData,
        });
      });
    }

    let alimonyData: Partial<AbstractCase> = {};
    if (nextApplicationStatus) {
      alimonyData.applicationStatus = nextApplicationStatus;
      this.statusFunctions[applicationStatus]?.();
    }

    if (_has(fields, "alimony")) {
      alimonyData = _merge(alimonyData, _pick(formData, fields.alimony));
      alimonyData = this.handleAlimonyFields(alimonyData);
    }

    await updateMutation.mutateAsync({ uri: getProductData(product.name, "apiUri"), id: product.id, data: alimonyData });
  };

  handleAlimonyFields = (data: Partial<AbstractCase>) => {
    let resultData = _cloneDeep(data);
    _forEach(this.conversionFields, (conversionField) => {
      const { path, convertTo } = conversionField;
      if (_has(resultData, path)) {
        _set(resultData, path, convertTo(_get(resultData, path)));
      }
    });

    _forEach(resultData, (value, key) => {
      if (_includes(this.booleanFields, key)) {
        resultData[key] = toBoolean(value);
      }
      if (_includes(this.numberFields, key)) {
        resultData[key] = parseInt(value, 10);
      }
    });

    return resultData;
  };

  prepareFormData = (product: AbstractCase, customer: Customer) => {
    let resultData = _cloneDeep(product);
    resultData.customer = _cloneDeep(customer);
    _forEach(this.conversionFields, (conversionField) => {
      const { path, convertFrom } = conversionField;
      if (_has(resultData, path)) {
        _set(resultData, path, convertFrom(_get(resultData, path)));
      }
    });

    _forEach(this.booleanFields, (path) => {
      if (_has(resultData, path) && _get(resultData, path) !== null) {
        _set(resultData, path, fromBoolean(_get(resultData, path)));
      }
    });

    _forEach(this.numberFields, (path) => {
      if (_has(resultData, path) && _get(resultData, path) !== null) {
        _set(resultData, path, _get(resultData, path) + "");
      }
    });

    _forEach(resultData.children, (child, index) => {
      if (resultData.children[index].birthDate !== null) {
        resultData.children[index].birthDate = fromDate(resultData.children[index].birthDate);
      }
    });

    return resultData;
  };

  getAvailableSteps = (formData: Record<string, any>, product: AbstractCase) => {
    let availableSteps = [];

    const useFormSteps = isFlexServiceProduct(product) ? formStepsFlexService : formSteps;

    availableSteps.push(useFormSteps[applicationStatuses.APPLICATION_STATUS_BASIC]);
    if (formData.basicChoice !== "other") {
      availableSteps.push(useFormSteps[applicationStatuses.APPLICATION_STATUS_ALIMONY]);
      if (formData.numberOfChildren > 0) {
        availableSteps.push(useFormSteps[applicationStatuses.APPLICATION_STATUS_CHILDREN]);
      }
      availableSteps.push(useFormSteps[applicationStatuses.APPLICATION_STATUS_PETITIONER]);
      availableSteps.push(useFormSteps[applicationStatuses.APPLICATION_STATUS_PARTNER]);
    }
    availableSteps.push(useFormSteps[applicationStatuses.APPLICATION_STATUS_MESSAGE]);
    availableSteps.push(useFormSteps[applicationStatuses.APPLICATION_STATUS_DOCUMENTS]);
    availableSteps.push(useFormSteps[applicationStatuses.APPLICATION_STATUS_CONTACT]);
    if (!isFlexServiceProduct(product)) {
      availableSteps.push(useFormSteps[applicationStatuses.APPLICATION_STATUS_INSURANCE]);
    }
    availableSteps.push(useFormSteps[applicationStatuses.APPLICATION_STATUS_SUMMARY]);

    return availableSteps;
  };
}

export default new FormService();
